import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Plus, MessageSquare, TrendingUp, Clock, Users } from 'lucide-react';
import { ForumLayout } from './ForumLayout';
import { TopicsList } from './TopicsList';
import { CreateTopicDialog } from './CreateTopicDialog';
import { Topic, YorubaReaction } from '../types';
import { toast } from 'sonner';
import { getMemberDisplayName, getAuthorDisplayName } from '@/utils/memberUtils';
import { awardXP, getXPForAction } from '@/utils/gamificationUtils';
import GamificationDashboard from '../GamificationDashboard';
import RealTimeChat from '../RealTimeChat';
import YorubaReactions from '../YorubaReactions';

interface HubSectionProps {
  isAdmin: boolean;
}

export default function HubSection({ isAdmin }: HubSectionProps) {
  const [isCreateTopicOpen, setIsCreateTopicOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('trending');
  const [topics, setTopics] = useState<Topic[]>([]);
  const [loading, setLoading] = useState(true);
  const [memberData, setMemberData] = useState<any>(null);
  const [membersCache, setMembersCache] = useState<Record<string, any>>({});
  const { user } = useAuth();

  // Fetch member data when user is available
  useEffect(() => {
    if (user) {
      fetchMemberData();
    }
  }, [user]);

  // Add real-time subscription for topics
  useEffect(() => {
    const channel = supabase
      .channel('hub_topics_changes')
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'hub_topics'
      }, () => {
        console.log('New topic received');
        fetchTopics(activeTab);
      })
      .subscribe();
    
    return () => {
      supabase.removeChannel(channel);
    };
  }, [activeTab]);

  useEffect(() => {
    fetchTopics(activeTab);
  }, [activeTab]);

  const fetchMemberData = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('community_members')
        .select('*')
        .eq('id', user.id)
        .single();
      
      if (error) throw error;
      setMemberData(data);
    } catch (err) {
      console.error('Error fetching member data:', err);
    }
  };

  const fetchTopics = async (filter: string) => {
    setLoading(true);
    try {
      console.log('Fetching topics with filter:', filter);
      
      let query = supabase
        .from('hub_topics')
        .select('*');

      if (filter === 'trending') {
        query = query.order('view_count', { ascending: false });
      } else if (filter === 'latest') {
        query = query.order('created_at', { ascending: false });
      }

      const { data, error } = await query.limit(50);

      if (error) {
        console.error('Error fetching topics:', error);
        toast.error('Failed to load topics');
        setLoading(false);
        return;
      }

      console.log('Fetched topic data:', data);
      
      if (!data || data.length === 0) {
        console.log('No topics found');
        setTopics([]);
        setLoading(false);
        return;
      }

      // Get all author IDs from topics
      const authorIds = [...new Set(data.map(topic => topic.author_id))];
      
      // Fetch member data for all authors in one batch
      const { data: memberData, error: memberError } = await supabase
        .from('community_members')
        .select('id, nickname, full_name, avatar_url')
        .in('id', authorIds);
      
      if (memberError) {
        console.error('Error fetching member data:', memberError);
      }
      
      // Create a lookup map for member data
      const memberMap: Record<string, any> = {};
      if (memberData) {
        memberData.forEach(member => {
          memberMap[member.id] = member;
        });
      }
      
      setMembersCache(memberMap);

      // Calculate reply count and fetch reactions for each topic
      const topicsWithRepliesAndReactions = await Promise.all(data.map(async (item: any) => {
        const [replyCountResult, reactionsResult] = await Promise.all([
          supabase
            .from('hub_replies')
            .select('*', { count: 'exact', head: true })
            .eq('topic_id', item.id),
          supabase
            .from('yoruba_reactions')
            .select('*')
            .eq('target_type', 'topic')
            .eq('target_id', item.id)
        ]);
        
        const replyCount = replyCountResult.count || 0;
        const reactions = reactionsResult.data || [];
        
        return {
          ...item,
          reply_count: replyCount,
          reactions: reactions as YorubaReaction[]
        };
      }));

      // Transform the data to match our Topic interface using member data
      const formattedTopics: Topic[] = topicsWithRepliesAndReactions.map((item: any) => {
        const member = memberMap[item.author_id];
        const displayName = member 
          ? (member.nickname || member.full_name) 
          : `User ${item.author_id.substring(0, 6)}`;
        
        return {
          id: item.id,
          title: item.title,
          content: item.content,
          author: {
            id: item.author_id,
            name: displayName,
            avatar: member?.avatar_url || null
          },
          category: item.category || 'General',
          created_at: item.created_at,
          reply_count: item.reply_count || 0,
          view_count: item.view_count || 0,
          last_activity: item.last_activity || item.created_at,
          reactions: item.reactions || []
        };
      });

      setTopics(formattedTopics);
    } catch (err) {
      console.error('Failed to fetch topics:', err);
      toast.error('Failed to load topics');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTopic = async (newTopic: Omit<Topic, 'id' | 'author' | 'created_at' | 'reply_count' | 'view_count' | 'last_activity' | 'reactions'>) => {
    if (!user) {
      toast.error('You need to be logged in to create topics');
      return;
    }

    try {
      console.log('Creating topic with data:', newTopic);
      
      await supabase.rpc('ensure_profile_exists', { user_id: user.id });

      if (!memberData) {
        await fetchMemberData();
      }
      
      const displayName = getAuthorDisplayName(memberData);

      console.log('Creating new hub_topic');
      const { data: topicData, error: topicError } = await supabase
        .from('hub_topics')
        .insert({
          title: newTopic.title,
          content: newTopic.content,
          category: newTopic.category,
          author_id: user.id,
          last_activity: new Date().toISOString(),
        })
        .select()
        .single();

      if (topicError) {
        console.error('Error creating topic:', topicError);
        toast.error('Failed to create topic. Please try again.');
        return;
      }

      // Award XP for creating topic
      await awardXP(user.id, getXPForAction('create_topic'), 'create_topic');

      console.log('Topic created successfully:', topicData);
      toast.success('Topic created successfully! +20 XP earned!');

      setIsCreateTopicOpen(false);
    } catch (error) {
      console.error('Error creating topic:', error);
      toast.error('Failed to create topic due to an unexpected error');
    }
  };

  const handleReactionUpdate = (topicId: string, reactions: YorubaReaction[]) => {
    setTopics(prev => prev.map(topic => 
      topic.id === topicId 
        ? { ...topic, reactions }
        : topic
    ));
  };

  return (
    <div className="space-y-6">
      {/* Gamification Dashboard */}
      <GamificationDashboard />
      
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <div className="flex justify-between items-center mb-4">
          <TabsList className="bg-white/60 backdrop-blur border">
            <TabsTrigger value="trending" className="flex items-center gap-1 data-[state=active]:bg-omoduduwa-terracotta data-[state=active]:text-white">
              <TrendingUp size={16} /> Trending
            </TabsTrigger>
            <TabsTrigger value="latest" className="flex items-center gap-1 data-[state=active]:bg-omoduduwa-terracotta data-[state=active]:text-white">
              <Clock size={16} /> Latest
            </TabsTrigger>
            <TabsTrigger value="chat" className="flex items-center gap-1 data-[state=active]:bg-omoduduwa-terracotta data-[state=active]:text-white">
              <Users size={16} /> Live Chat
            </TabsTrigger>
          </TabsList>
          
          {user && (
            <Button 
              onClick={() => setIsCreateTopicOpen(true)} 
              className="bg-omoduduwa-terracotta hover:bg-omoduduwa-terracotta/90 text-white flex items-center gap-2"
            >
              <Plus size={16} /> New Topic
            </Button>
          )}
        </div>

        <TabsContent value="trending" className="mt-0">
          <ForumLayout title="Trending Topics">
            <TopicsList 
              topics={topics} 
              loading={loading}
              onReactionUpdate={handleReactionUpdate}
            />
          </ForumLayout>
        </TabsContent>
        
        <TabsContent value="latest" className="mt-0">
          <ForumLayout title="Latest Topics">
            <TopicsList 
              topics={topics} 
              loading={loading}
              onReactionUpdate={handleReactionUpdate}
            />
          </ForumLayout>
        </TabsContent>

        <TabsContent value="chat" className="mt-0">
          <ForumLayout title="Community Live Chat">
            <RealTimeChat 
              hubId="general" 
              hubName="General Community" 
            />
          </ForumLayout>
        </TabsContent>
      </Tabs>

      <CreateTopicDialog 
        open={isCreateTopicOpen} 
        onOpenChange={setIsCreateTopicOpen}
        onCreateTopic={handleCreateTopic}
      />
    </div>
  );
}
